import React from "react";
import {Controller, useFormContext} from "react-hook-form";
import {InputLabel, Select, SelectOptionProps, TextareaField, TextField} from "peggirkit";
import {FormattedMessage, useIntl} from "react-intl";
import TextAddOn from "peggirkit/dist/components/Input/Field/TextField/TextFieldAddOn/TextAddOn";
import {emptyToNull, getInputCurrencyProps, getInputValidationProps} from "@/data/Validation";
import {Menu, Product} from "@/data/entities/menu";
import {ProductEditValues, productEditValuesFormValidation, VAT_RATES} from "@/data/entities/menuEditor";
import {label} from "@/utils/LabelUtils";
import BooleanSelect from "@widgets/menuEditor/menu/product/fields/BooleanSelect";
import AvailabilityField from "@widgets/menuEditor/menu/product/fields/AvailabilityField";
import AlcoholicField from "@widgets/menuEditor/menu/product/fields/AlcoholicField";
import FlagsField from "@widgets/menuEditor/menu/product/fields/FlagsField";
import OptionGroupsField from "@widgets/menuEditor/menu/product/fields/OptionGroupsField";

const vatOptions: SelectOptionProps<number>[] = VAT_RATES.map(rate => ({
    value: rate,
    displayValue: `${rate}%`,
}));

type Props = {
    product: Product | null,
    menu: Menu,
    handleSubmitHandler: () => void,
    disabledState: boolean,
};

/**
 * All fields of a product. The values are set by the modal when it opens.
 */
const EditProductForm = ({product, menu, handleSubmitHandler, disabledState}: Props) => {
    const intl = useIntl();
    const {control, formState: {errors}, register} = useFormContext<ProductEditValues>();

    return (
        <form
            className={"w-full grid grid-cols-2 gap-5 md:gap-8 my-6 md:my-8"}
            onSubmit={handleSubmitHandler}
        >
            {
                product &&
                <TextField
                    containerClassname={"col-span-2"}
                    type={"number"}
                    label={<InputLabel><FormattedMessage id={"id"}/></InputLabel>}
                    tip={<FormattedMessage id={"internalIdReferencePurposes"}/>}
                    defaultValue={product.id}
                    disabled={true}
                />
            }

            {/* Name */}
            <TextField
                containerClassname={"col-span-2"}
                type={"text"}
                label={label("name", true)}
                disabled={disabledState}
                error={errors.name && errors.name.message}
                {...register("name", {
                    ...getInputValidationProps(productEditValuesFormValidation.name, intl),
                })}
            />

            {/* Description */}
            <TextareaField
                containerClassname={"col-span-2"}
                rows={2}
                label={label("description", false)}
                disabled={disabledState}
                error={errors.description && errors.description.message}
                {...register("description", {
                    ...getInputValidationProps(productEditValuesFormValidation.description, intl),
                    setValueAs: emptyToNull,
                })}
            />

            {/* Price delivery */}
            <TextField
                type={"text"}
                inputMode={"decimal"}
                label={label("deliveryPrice", true)}
                leading={TextAddOn({text: "€"})}
                placeholder={"20,00"}
                disabled={disabledState}
                error={errors.price && errors.price.message}
                {...register("price", {
                    ...getInputValidationProps(productEditValuesFormValidation.price, intl),
                    ...getInputCurrencyProps(intl),
                })}
            />

            {/* Price takeaway */}
            <TextField
                type={"text"}
                inputMode={"decimal"}
                label={label("takeawayPrice", true)}
                leading={TextAddOn({text: "€"})}
                placeholder={"18,50"}
                disabled={disabledState}
                error={errors.priceTakeaway && errors.priceTakeaway.message}
                {...register("priceTakeaway", {
                    ...getInputValidationProps(productEditValuesFormValidation.priceTakeaway, intl),
                    ...getInputCurrencyProps(intl),
                })}
            />

            {/* Deposit */}
            <TextField
                type={"text"}
                inputMode={"decimal"}
                label={label("deposit", false)}
                leading={TextAddOn({text: "€"})}
                placeholder={"0,00"}
                disabled={disabledState}
                error={errors.deposit && errors.deposit.message}
                {...register("deposit", {
                    ...getInputValidationProps(productEditValuesFormValidation.deposit, intl),
                    ...getInputCurrencyProps(intl),
                })}
            />

            {/* SUP-fee */}
            <TextField
                type={"text"}
                inputMode={"decimal"}
                label={label("plasticFee", false)}
                leading={TextAddOn({text: "€"})}
                placeholder={"0,00"}
                disabled={disabledState}
                error={errors.supFee && errors.supFee.message}
                {...register("supFee", {
                    ...getInputValidationProps(productEditValuesFormValidation.supFee, intl),
                    ...getInputCurrencyProps(intl),
                })}
            />

            {/* VAT rate */}
            <Controller
                control={control}
                name={"vat"}
                render={({field: {value, onChange}}) => (
                    <Select<number>
                        label={label("vatRate", true)}
                        options={vatOptions}
                        value={vatOptions.find(option => option.value === value) ?? null}
                        displayValueEmpty={"-"}
                        onChange={(selectedOption: SelectOptionProps<number>) => onChange(selectedOption.value)}
                        disabled={disabledState}
                    />
                )}
            />

            {/* Sold out */}
            <Controller
                control={control}
                name={"soldout"}
                render={({field: {value, onChange}}) => (
                    <BooleanSelect
                        label={label("stockStatus", true)}
                        trueLabel={intl.formatMessage({id: "soldOut"})}
                        falseLabel={intl.formatMessage({id: "inStock"})}
                        value={value}
                        onChange={onChange}
                        disabled={disabledState}
                    />
                )}
            />

            {/* Availability */}
            <AvailabilityField disabled={disabledState}/>

            {/* Alcoholic */}
            <AlcoholicField disabled={disabledState}/>

            {/* Flags */}
            <div className={"col-span-2"}>
                <FlagsField disabled={disabledState}/>
            </div>

            {/* Option groups */}
            <div className={"col-span-2"}>
                <OptionGroupsField
                    menu={menu}
                    disabled={disabledState}
                />
            </div>
        </form>
    );
};

export default EditProductForm;